import React, { useMemo, useRef, useState } from 'react';
import { Container, Typography, Stack, Card, CardContent, Box, ToggleButton, ToggleButtonGroup, TextField, Tooltip, Chip } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { teamAvailability } from '../data/availabilityData';

export default function Availability() {
  const calendarRef = useRef(null);
  const [viewMode, setViewMode] = useState('dayGridMonth'); // 'dayGridMonth' | 'dayGridWeek' | 'timeline'
  const [timelineMonth, setTimelineMonth] = useState(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });

  const members = teamAvailability.members;

  const events = useMemo(() => {
    const evts = [];
    for (const m of members) {
      for (const rng of m.unavailable || []) {
        evts.push({
          id: `${m.id}-${rng.start}-${rng.end}`,
          title: `${m.name} • ${rng.task || 'Unavailable'}`,
          start: rng.start,
          end: new Date(new Date(rng.end).getTime() + 24*60*60*1000).toISOString().slice(0,10), // end is exclusive
          extendedProps: { member: m.name, role: m.role, task: rng.task },
          backgroundColor: '#ef4444',
          borderColor: '#ef4444',
          textColor: '#ffffff'
        });
      }
    }
    return evts;
  }, [members]);

  const handleToggle = (_e, next) => {
    if (!next) return;
    setViewMode(next);
    const api = calendarRef.current?.getApi();
    if (api && (next === 'dayGridMonth' || next === 'dayGridWeek')) api.changeView(next);
  };

  const timeline = useMemo(() => {
    if (viewMode !== 'timeline') return null;
    const [yStr, mStr] = timelineMonth.split('-');
    const year = parseInt(yStr, 10);
    const mIdx = parseInt(mStr, 10) - 1;
    const first = new Date(Date.UTC(year, mIdx, 1));
    const last = new Date(Date.UTC(year, mIdx + 1, 0));
    const days = [];
    const d = new Date(first);
    while (d <= last) {
      days.push(d.toISOString().slice(0, 10));
      d.setUTCDate(d.getUTCDate() + 1);
    }
    const palette = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
    const items = [];
    teamAvailability.members.forEach((m, idx) => {
      (m.unavailable || []).forEach((rng, rIdx) => {
        const start = new Date(rng.start);
        const end = new Date(rng.end);
        if (end < first || start > last) return;
        const clampedStart = start < first ? first : start;
        const clampedEnd = end > last ? last : end;
        const startISO = clampedStart.toISOString().slice(0, 10);
        const endISO = clampedEnd.toISOString().slice(0, 10);
        const startIndex = days.indexOf(startISO);
        const endIndex = days.indexOf(endISO);
        if (startIndex === -1 || endIndex === -1) return;
        items.push({
          key: `${m.id}-${rIdx}`,
          member: m.name,
          task: rng.task || 'Unavailable',
          color: palette[idx % palette.length],
          startCol: startIndex + 1,
          endCol: endIndex + 2,
        });
      });
    });
    return { days, items };
  }, [viewMode, timelineMonth]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h4" textAlign="center">Team Availability</Typography>
        <Stack direction="row" justifyContent="center" spacing={2} alignItems="center">
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleToggle} size="small">
            <ToggleButton value="dayGridMonth">Month</ToggleButton>
            <ToggleButton value="dayGridWeek">Week</ToggleButton>
            <ToggleButton value="timeline">Timeline</ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'timeline' && (
            <TextField type="month" size="small" value={timelineMonth} onChange={(e) => setTimelineMonth(e.target.value)} />
          )}
        </Stack>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          {viewMode !== 'timeline' ? (
          <Box sx={{
            '& .fc': { fontFamily: 'inherit' },
            '& .fc .fc-daygrid-day.fc-day-sat, & .fc .fc-daygrid-day.fc-day-sun': {
              backgroundColor: '#fafafa'
            },
            '& .fc .fc-toolbar': { display: 'none' },
            '& .fc .fc-daygrid-event': { borderRadius: 6, padding: '0 4px' }
          }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView={viewMode}
              headerToolbar={false}
              events={events}
              height="auto"
              dayMaxEventRows={3}
              eventDidMount={(info) => {
                if (info.event.extendedProps?.task) {
                  info.el.title = `${info.event.extendedProps.member} • ${info.event.extendedProps.task}`;
                }
              }}
            />
          </Box>
          ) : (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: `64px repeat(${timeline?.days.length || 0}, 1fr)`, alignItems: 'center' }}>
              <Box />
              {timeline?.days.map((iso) => (
                <Box key={iso} sx={{ textAlign: 'center', py: 0.5, borderRight: '1px solid', borderColor: 'divider', color: '#64748b' }}>
                  <Typography variant="caption">{new Date(iso).getUTCDate()}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: `64px repeat(${timeline?.days.length || 0}, 1fr)`, alignItems: 'stretch' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1 }}>
                <Typography variant="caption" color="text.secondary">All</Typography>
              </Box>
              <Box sx={{ gridColumn: `span ${timeline?.days.length || 0}`, position: 'relative', borderTop: '1px dashed', borderColor: 'divider', minHeight: 48 }}>
                {timeline?.items.map((it) => (
                  <Tooltip key={it.key} title={`${it.member} • ${it.task}`} arrow>
                    <Box sx={{ position: 'absolute', left: 0, top: 8, height: 28, display: 'flex', alignItems: 'center', px: 1, color: '#fff', fontSize: 12, borderRadius: 2, backgroundColor: it.color, gridColumn: `${it.startCol} / ${it.endCol}` }} style={{
                      // Translate grid columns to absolute positioning
                      width: `calc((100% / ${(timeline?.days.length || 1)}) * ${(it.endCol - it.startCol)} - 2px)`,
                      transform: `translateX(calc((100% / ${(timeline?.days.length || 1)}) * ${(it.startCol - 1)}))`
                    }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.member.split(' ')[0]} • {it.task}</span>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {teamAvailability.members.map((m, idx) => (
                <Chip key={m.id} size="small" label={m.name} sx={{ bgcolor: 'transparent', border: '1px solid', borderColor: 'divider' }} icon={<span style={{ width: 12, height: 12, background: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'][idx % 5], display: 'inline-block', borderRadius: 2 }} />} />
              ))}
            </Stack>
          </Box>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 16, height: 16, backgroundColor: 'error.main', borderRadius: 0.5 }} />
              <Typography variant="caption">Unavailable</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 16, height: 16, backgroundColor: '#fafafa', border: '1px dashed', borderColor: 'divider', borderRadius: 0.5 }} />
              <Typography variant="caption">Weekend</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}


